import { cn } from '../../lib/cn';

/**
 * Barra de progreso.
 *
 * Tiene **dos lecturas, y hay que elegir**. En un medidor de consumo, llegar al
 * final es malo: por eso el color lo fija el propio valor —azul hasta el 80 %,
 * ámbar hasta el 90 % y rojo por encima—. En una barra de avance es al revés:
 * el 100 % es la buena noticia, y pintar de rojo una solicitud completa diría
 * exactamente lo contrario de lo que pasó.
 *
 * `tone` distingue las dos. Por defecto es `usage`, que es como se comportaba
 * antes de existir esta opción, así que el medidor de almacenamiento no cambia.
 *
 * Que el umbral viva aquí evita que cada pantalla invente el suyo y que la
 * misma cifra se pinte distinta en dos sitios.
 *
 * El relleno normal usa `primary-soft` en vez del degradado de marca: contra
 * el carril `surface-2` el degradado daba 2,85:1 en su extremo azul y 2,57:1
 * en el morado, por debajo del 3:1 que 1.4.11 exige. Una barra que no se
 * distingue de su carril no informa de nada, que es justo su único trabajo.
 */
export function Progress({
  value,
  max = 100,
  label,
  /** Lo que oye quien no ve la barra. Sin esto sólo se anuncia «45 %». */
  valueText,
  tone = 'usage',
  className,
}: {
  value: number;
  max?: number;
  label?: string;
  valueText?: string;
  /** `usage`: llenarse es malo. `completion`: llenarse es el objetivo. */
  tone?: 'usage' | 'completion';
  className?: string;
}) {
  const porcentaje = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const color =
    tone === 'completion'
      ? porcentaje >= 100
        ? 'bg-success'
        : 'bg-primary-soft'
      : porcentaje >= 90
        ? 'bg-danger'
        : porcentaje >= 80
          ? 'bg-warning'
          : 'bg-primary-soft';

  return (
    <div
      className={cn('h-2 w-full overflow-hidden rounded-full bg-surface-2', className)}
      role="progressbar"
      aria-valuenow={Math.round(porcentaje)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={valueText}
      aria-label={label}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-500', color)}
        style={{ width: `${porcentaje}%` }}
      />
    </div>
  );
}
